import { Box, Button as ECButton, Spacer } from '@eatclubapp/ec-component-library';
import { useTheme } from '@material-ui/core';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { Button } from '../../../../components/Button';
import { TextField } from '../../../../components/TextField';
import { updateBookingAction } from '../../../../data/actions/bookingAction';
import { addMessageAction } from '../../../../data/actions/messagesAction';
import { makeGenericApiCall } from '../../../../data/actions/shared/helpers';
import { bookingKeys, bookingPropTypes } from '../../../../data/models/Booking';
import { restaurantPropTypes } from '../../../../data/models/Restaurant';
import { findObjectByProperty, isEmpty, trim } from '../../../../utils/helpers';
import { MOCK_VOUCHER, isPreviewMode } from '../../../../utils/previewMode';
import useStyles from './VoucherReferenceStyles';

const AddVoucher = ({
  booking, // The local booking being created/edited
  restaurant,
  updateBooking,
}) => {
  const theme = useTheme();
  const classes = useStyles();

  const [error, setError] = useState(false);
  const [checkingVoucher, setCheckingVoucher] = useState(false);
  const [showVoucherField, setShowVoucherField] = useState(false);
  const [value, setValue] = useState(null);

  const submitVoucher = async () => {
    const trimmedValue = trim(value);

    // Validate they entered something
    if (isEmpty(trimmedValue)) {
      setError('Please enter the voucher number');
      return;
    }

    // Validate the voucher wasn't already added
    if (findObjectByProperty(booking?.[bookingKeys.voucherReferences], trimmedValue, 'voucher')) {
      setShowVoucherField(false);
      setValue(null);
      return;
    }

    // Check voucher is valid. In preview mode any reference resolves to the mock voucher
    setCheckingVoucher(true);
    const voucherResponse = isPreviewMode()
      ? { valid: true, balance: MOCK_VOUCHER.amount, amount: MOCK_VOUCHER.amount }
      : await makeGenericApiCall('post', 'gift-vouchers/getVoucherByRef', {
          voucherRef: trimmedValue,
        });

    // If invalid, show error
    if (voucherResponse?.valid === false) {
      setCheckingVoucher(false);
      setError(voucherResponse?.reason);
      return;
    }

    // On success
    setShowVoucherField(false);
    setCheckingVoucher(false);
    setValue(null);

    // Update the state
    updateBooking(bookingKeys.voucherReferences, [
      ...booking?.[bookingKeys.voucherReferences],
      {
        voucher: trimmedValue,
        valid: voucherResponse.valid,
        amount: voucherResponse.balance, // Response has both balance and amount. I think amount is the original amount though
        reason: voucherResponse?.reason,
      },
    ]);
  };

  return (
    <>
      {showVoucherField ? (
        <Spacer direction='vertical' gap={16}>
          <Box>{restaurant.data.addVoucherLabel}</Box>
          <Spacer direction='vertical' gap={8}>
            <Spacer>
              <TextField
                // label='Add voucher reference'
                placeholder='Voucher reference'
                value={value || ''}
                onChange={(e) => {
                  setError(null);
                  setValue(e.target.value);
                }}
                type='text'
                InputProps={{ disableUnderline: true, height: '50px', padding: 0 }}
                style={{
                  height: '50px',
                }}
                className={classes.voucherReference}
                autoFocus
                name='voucher'
                autoComplete='off'
              />
              <Box className={classes.buttonContainer}>
                <Button
                  color='primary'
                  loading={checkingVoucher}
                  onClick={submitVoucher}
                  style={{
                    height: '50px',
                    width: '100%',
                  }}
                >
                  Submit
                </Button>
              </Box>
            </Spacer>
            {restaurant.data.addVoucherSubtext && (
              <Box style={{ fontSize: '14px' }}>{restaurant.data.addVoucherSubtext}</Box>
            )}
            {error && <Box style={{ color: theme.colors.valencia, fontSize: '12px' }}>{error}</Box>}
          </Spacer>
        </Spacer>
      ) : (
        <ECButton
          type='text'
          onClick={() => setShowVoucherField(true)}
          // InputProps={{ disableUnderline: false }}
          style={{
            button: {
              textDecoration: 'underline',
              height: '50px',
            },
          }}
        >
          + Add voucher
        </ECButton>
      )}
    </>
  );
};

AddVoucher.propTypes = {
  booking: bookingPropTypes.isRequired,
  restaurant: restaurantPropTypes.isRequired,
  updateBooking: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => ({
  booking: state.booking,
  restaurant: state.restaurant,
});

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      addMessage: addMessageAction,
      updateBooking: updateBookingAction,
    },
    dispatch,
  );

export default connect(mapStateToProps, mapDispatchToProps)(AddVoucher);
